import { useEffect, useState } from "react";
import { TbPlus, TbEdit, TbExternalLink } from "react-icons/tb";
import { toast } from "react-toastify";
import { Auth } from "aws-amplify";
import { Link } from "react-router-dom";
import appConfig from "../../../../config/app-config";
import { PERSON_PRODUCTS, PERSON_PRODUCT_LABELS } from "../../../../constants/personProducts";
import SignatoryModal, { SignatoryFormData, SignAsInfo } from "./SignatoryModal";
import { buildSignatoryPayload } from "./signatoryPayload";

const { address: PERSON_API_URL, key: PERSON_API_KEY } = appConfig.apis.personApiEnv;

interface SignatoryFromApi {
    id: number;
    individualUuid: string;
    fullName: string;
    socialId: string;
    email: string;
    mobile: string;
    position: string;
    allowedOperations: Array<{ operation: string; representationFormat: string } | string>;
    proxyExpirationDate?: string;
    proxyAllowedValue?: number;
    signAsEntries: { signAs: string; useDigitalCertificate: boolean }[];
    signatureProfile: string;
    isActive: boolean;
}

const REPRESENTATION_FORMAT_MAP: Record<string, string> = {
    'ISOLATED_OR_JOINT': 'Isolada ou Em Conjunto',
    'ISOLATED': 'Isoladamente',
    'ISOLATED_AND_OR_JOINT': 'Isolada e/ou em conjunto',
    'JOINT_TWO_DIRECTORS': 'Conjunto de 2 signatários',
    'DEPENDS_OBLIGATION': 'Depende Obrig/Ope',
    'JOINT': 'Em conjunto',
    'ISOLATED_ATTORNEY': 'Isolad. Procurador',
    'JOINT_TWO_ATTORNEYS': 'Conjunto de 2 Procuradores',
    'CONDITIONAL_VALUE_OBLIGATION': 'Condicionada por valor',
    'TWO_PERSONS': '2 Pessoas',
    'THREE_PERSONS': '3 Pessoas'
};

interface LegalPersonSignatoriesTabProps {
    legalPersonUuid: string;
    requiresSignatoryVerification?: boolean;
    onVerificationToggle?: (enabled: boolean) => void;
}

const LegalPersonSignatoriesTab: React.FC<LegalPersonSignatoriesTabProps> = ({ legalPersonUuid, requiresSignatoryVerification = false, onVerificationToggle }) => {
    const [signatories, setSignatories] = useState<SignatoryFromApi[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSignatory, setEditingSignatory] = useState<SignatoryFormData | null>(null);
    const [loading, setLoading] = useState(true);
    const [verificationFlag, setVerificationFlag] = useState(requiresSignatoryVerification);
    const [togglingFlag, setTogglingFlag] = useState(false);

    const getHeaders = async () => {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        return {
            'x-api-key': PERSON_API_KEY,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    };

    const loadSignatories = async () => {
        if (!legalPersonUuid) return;
        try {
            const headers = await getHeaders();
            const res = await fetch(`${PERSON_API_URL}/legal-persons/${legalPersonUuid}/signatories`, { headers });
            if (res.ok) {
                const data = await res.json();
                setSignatories(data.signatories || []);
            }
        } catch (e) {
            console.error('Error loading signatories:', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadSignatories(); }, [legalPersonUuid]);

    useEffect(() => { setVerificationFlag(requiresSignatoryVerification); }, [requiresSignatoryVerification]);

    const handleToggleVerification = async () => {
        setTogglingFlag(true);
        try {
            const headers = await getHeaders();
            const res = await fetch(`${PERSON_API_URL}/legal-persons/${legalPersonUuid}/signatory-verification`, {
                method: 'PUT', headers, body: JSON.stringify({ enabled: !verificationFlag })
            });
            if (!res.ok) throw new Error('Erro ao atualizar flag');
            const data = await res.json();
            setVerificationFlag(data.requiresSignatoryVerification);
            onVerificationToggle?.(data.requiresSignatoryVerification);
            toast.success(data.requiresSignatoryVerification ? 'Consultar Cadastro ativado' : 'Consultar Cadastro desativado');
        } catch (e) {
            toast.error('Erro ao atualizar flag');
        } finally {
            setTogglingFlag(false);
        }
    };

    const handleEdit = (sig: SignatoryFromApi) => {
        const operationsWithFormat = sig.allowedOperations.map((op: any) => {
            if (typeof op === 'object' && op.operation) {
                return { operation: op.operation, representationFormat: op.representationFormat || 'ISOLATED' };
            }
            // Compatibilidade com formato antigo (string simples)
            return { operation: op, representationFormat: 'ISOLATED' };
        });
        setEditingSignatory({
            id: sig.id,
            individualUuid: sig.individualUuid,
            fullName: sig.fullName,
            socialId: sig.socialId,
            position: sig.position,
            signAsEntries: sig.signAsEntries as SignAsInfo[],
            signatureProfile: sig.signatureProfile,
            operationsWithFormat,
            email: sig.email,
            mobilePhone: sig.mobile,
            proxyExpirationDate: sig.proxyExpirationDate || '',
            proxyAllowedValue: sig.proxyAllowedValue ?? '',
        });
        setIsModalOpen(true);
    };

    const handleNew = () => {
        setEditingSignatory(null);
        setIsModalOpen(true);
    };

    const handleSave = async (data: SignatoryFormData) => {
        try {
            const headers = await getHeaders();
            const payload = buildSignatoryPayload(data);

            if (data.id) {
                const res = await fetch(`${PERSON_API_URL}/legal-persons/${legalPersonUuid}/signatories/${data.id}`, {
                    method: 'PUT', headers, body: JSON.stringify(payload)
                });
                if (!res.ok) { throw new Error('Erro ao atualizar'); }
                toast.success(`Signatário ${data.fullName} atualizado!`);
            } else {
                const res = await fetch(`${PERSON_API_URL}/legal-persons/${legalPersonUuid}/signatories`, {
                    method: 'POST', headers, body: JSON.stringify(payload)
                });
                if (res.status === 409) {
                    toast.warn('Este signatário já está vinculado a esta empresa.');
                    throw new Error('DUPLICATE_SIGNATORY');
                }
                if (!res.ok) { throw new Error('Erro ao vincular'); }
                toast.success(`Signatário ${data.fullName} vinculado!`);
            }
            loadSignatories();
        } catch (e) {
            if (!(e instanceof Error && e.message === 'DUPLICATE_SIGNATORY')) {
                toast.error('Erro ao salvar signatário');
            }
            console.error(e);
            throw e;
        }
    };

    const handleToggleActive = async (sig: SignatoryFromApi) => {
        try {
            const headers = await getHeaders();
            const url = sig.isActive
                ? `${PERSON_API_URL}/legal-persons/${legalPersonUuid}/signatories/${sig.id}`
                : `${PERSON_API_URL}/legal-persons/${legalPersonUuid}/signatories/${sig.id}/activate`;
            const method = sig.isActive ? 'DELETE' : 'PUT';
            const res = await fetch(url, { method, headers });
            if (!res.ok) { throw new Error('Erro ao alterar status'); }
            toast.success(`Signatário ${sig.fullName} ${sig.isActive ? 'desativado' : 'reativado'}!`);
            loadSignatories();
        } catch (e) {
            toast.error('Erro ao alterar status do signatário');
        }
    };

    if (loading) return <div className="p-4 m-4 text-gray-500">Carregando signatários...</div>;

    return (
        <div className="p-4 m-4">
            {/* Consultar Cadastro toggle */}
            <div className="flex items-center justify-between mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div>
                    <span className="text-sm font-semibold text-amber-800">Consultar Cadastro</span>
                    <p className="text-xs text-amber-600">Ative para sinalizar ao jurídico que esta PJ requer verificação de signatários</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={verificationFlag} onChange={handleToggleVerification} disabled={togglingFlag} className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                    <span className="ml-2 text-xs font-medium text-gray-700">{verificationFlag ? 'Ativo' : 'Inativo'}</span>
                </label>
            </div>

            <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-700">
                    Signatários ({signatories.length})
                </h2>
                <button onClick={handleNew} className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors">
                    <TbPlus size={20} />
                    Incluir Signatário
                </button>
            </div>

            <div className="grid gap-4">
                {signatories.map((sig) => (
                    <div key={sig.id} className={`border rounded-lg p-4 transition-shadow ${sig.isActive ? 'bg-white border-gray-200 hover:shadow-md' : 'bg-gray-50 border-red-200 opacity-60'}`}>
                        <div className="flex justify-between items-start">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <h3 className="text-base font-semibold text-gray-800">{sig.fullName}</h3>
                                    {!sig.isActive && <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded font-medium">Inativo</span>}
                                </div>
                                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                                    <div>
                                        <span className="text-gray-500">CPF:</span>{" "}
                                        <span className="text-gray-800 font-medium">{sig.socialId}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Cargo:</span>{" "}
                                        <span className="text-gray-800">{sig.position}</span>
                                    </div>
                                    <div className="col-span-2">
                                        <span className="text-gray-500">Tipo(s):</span>{" "}
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {sig.signAsEntries.map((entry, idx) => (
                                                <span key={idx} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                                                    {entry.signAs}
                                                    {entry.useDigitalCertificate && <span className="text-green-600" title="Certificado Digital">🔐</span>}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="col-span-2">
                                        <span className="text-gray-500">Operações Permitidas:</span>{" "}
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {sig.allowedOperations
                                                .filter((op: any) => {
                                                    const opName = typeof op === 'object' ? op.operation : op;
                                                    return (PERSON_PRODUCTS as readonly string[]).includes(opName);
                                                })
                                                .map((op: any, idx: number) => {
                                                    const opName = typeof op === 'object' ? op.operation : op;
                                                    const opFormat = typeof op === 'object' ? op.representationFormat : null;
                                                    return (
                                                        <span key={idx} className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                                                            {PERSON_PRODUCT_LABELS[opName] || opName}{opFormat ? ` — ${REPRESENTATION_FORMAT_MAP[opFormat] || opFormat}` : ''}
                                                        </span>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Email:</span>{" "}
                                        <span className="text-gray-800">{sig.email}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Telefone:</span>{" "}
                                        <span className="text-gray-800">{sig.mobile}</span>
                                    </div>
                                    {sig.proxyExpirationDate && (
                                        <div>
                                            <span className="text-gray-500">Venc. Procuração:</span>{" "}
                                            <span className="text-gray-800">{new Date(sig.proxyExpirationDate + 'T00:00:00').toLocaleDateString('pt-BR')}</span>
                                        </div>
                                    )}
                                    {sig.proxyAllowedValue != null && (
                                        <div>
                                            <span className="text-gray-500">Valor Permitido:</span>{" "}
                                            <span className="text-gray-800">{sig.proxyAllowedValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-col gap-3 ml-4">
                                <button onClick={() => handleEdit(sig)} className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Editar">
                                    <TbEdit size={18} />
                                </button>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input type="checkbox" checked={sig.isActive} onChange={() => handleToggleActive(sig)} className="sr-only peer" />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                                    <span className="ml-2 text-xs font-medium text-gray-700">{sig.isActive ? 'Ativo' : 'Inativo'}</span>
                                </label>
                                <Link to={`/person/individual/edit/${sig.individualUuid}?tab=0`} target="_blank" className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline" title="Ver cadastro completo da pessoa física">
                                    <TbExternalLink size={14} /> Ver cadastro
                                </Link>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {signatories.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                    <p className="mb-4">Nenhum signatário cadastrado</p>
                    <button onClick={handleNew} className="text-blue-600 hover:text-blue-700 underline">
                        Incluir primeiro signatário
                    </button>
                </div>
            )}

            <SignatoryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSave} editData={editingSignatory} legalPersonUuid={legalPersonUuid} />
        </div>
    );
};

export default LegalPersonSignatoriesTab;
